/**
 * Back-compat barrel for the original 5-primitive API (Button/Card/Badge/Spinner/
 * CurrencyDisplay/LocaleSwitcher) plus the full design-system set under ./ui/*.
 * Inherited feature pages import from here; new pages import from './ui/index.js'.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Money } from './ui/Money.js';
import { StatusPill, statusTone } from './ui/StatusPill.js';

export * from './ui/index.js';

/** Humanize a snake_case status into Title Case for fallback labels. */
function humanize(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Status chip (back-compat). Accepts a raw status string; tries a few common
 * i18n namespaces for a localized label, else humanizes the status.
 */
export function Badge({ status, label }: { status: string; label?: ReactNode }): ReactNode {
  const { t } = useTranslation();
  if (label === undefined) {
    for (const key of [
      `memberships.status.${status}`,
      `billing.b2b.status.${status}`,
      `classes.session.status.${status}`,
      `members.status.${status}`,
    ]) {
      const v = t(key, { defaultValue: '' });
      if (v) {
        label = v;
        break;
      }
    }
  }
  return <StatusPill status={status} tone={statusTone(status)} label={label ?? humanize(status)} />;
}

/** Money display (back-compat): integer minor units (денари) → localized MKD. */
export function CurrencyDisplay({ minorUnits }: { minorUnits: number }): ReactNode {
  return <Money minor={minorUnits} />;
}
